import React, { useState, useEffect, useMemo } from 'react';
import { DataManager } from '../../services/dataManager';
import { AuthService } from '../../services/authService';
import Button from '../common/Button';
import ClientDetailView from './ClientDetailView';
import DataBackupManager from '../common/DataBackupManager';
import ReportsView from './ReportsView';
import KnowledgeBaseView from './KnowledgeBaseView';
import ToolkitView from './ToolkitView';
import CounselorManagementModal from './CounselorManagementModal';
import GroupCounselingModal from './GroupCounselingModal';
import SupervisionAccessLogModal from './SupervisionAccessLogModal';
import OrganizationManagementModal from './OrganizationManagementModal';
import PeopleCareAnalyticsView from '../analytics/PeopleCareAnalyticsView';
import PrivacyPolicyModal from './PrivacyPolicyModal';

const CounselorDashboard = ({ user, onLogout }) => {
    const [view, setView] = useState('list'); // 'list' | 'detail' | 'reports' | 'kb' | 'toolkit' | 'peopleCare'
    const [selectedKb, setSelectedKb] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [clients, setClients] = useState([]);
    const [counselorsList, setCounselorsList] = useState([]);
    const [filterCounselor, setFilterCounselor] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showCounselorModal, setShowCounselorModal] = useState(false);
    const [showOrgModal, setShowOrgModal] = useState(false);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [showAccessLogModal, setShowAccessLogModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [showNewClientModal, setShowNewClientModal] = useState(false);
    const [newClientForm, setNewClientForm] = useState({ name: '', email: '', firebaseUid: '', counselorId: user.id, testPassword: '', mode: 'manual' });
    
    // Inbox per Insight Pubblici
    const [pendingInsights, setPendingInsights] = useState([]);
    const [showInboxModal, setShowInboxModal] = useState(false);
    const [selectedInsightPreview, setSelectedInsightPreview] = useState(null);
    const [insightClientSelections, setInsightClientSelections] = useState({});
    const [copiedPublicLink, setCopiedPublicLink] = useState(false);
    const [meetingCounts, setMeetingCounts] = useState({});

    // Quick Reassign Modal state
    const [clientToReassign, setClientToReassign] = useState(null);
    const [quickReassignTargetCounselorId, setQuickReassignTargetCounselorId] = useState('');
    const [quickReassigning, setQuickReassigning] = useState(false);

    const isAdmin = Boolean(user.isAdmin || user.role === 'admin' || user.role === 'superadmin' || user.email?.toLowerCase() === 'nuvola@awarenesstool.it' || user.email?.toLowerCase() === 'marilisa.delve@gmail.com');
    const isOrgAdmin = Boolean(user.isOrgAdmin || user.role === 'org_admin' || user.email?.toLowerCase() === 'patrizia.marforio@gmail.com' || user.email?.toLowerCase() === 'marzia.macca@gmail.com' || user.email?.toLowerCase() === 'trebino.martina@gmail.com' || user.email?.toLowerCase() === 'patrizia.marchi.1969@gmail.com');
    const isSupervisor = isAdmin || isOrgAdmin;
    const hasBuenaVistaAccess = Boolean(
        isAdmin || 
        isOrgAdmin || 
        user.organizationId === 'buenavista' || 
        (Array.isArray(user.allowedOrganizations) && user.allowedOrganizations.includes('buenavista')) ||
        (user.organizationName || '').toLowerCase().includes('buenavista')
    );

    const fetchClients = async () => {
        try {
            setLoading(true);
            
            // Load counselors list, clients and all meetings in parallel
            const [counselors, clientData, allMeetings] = await Promise.all([
                DataManager.getAllCounselors(),
                DataManager.getClients((isAdmin || isOrgAdmin) ? filterCounselor : null),
                DataManager.getAllMeetings()
            ]);
            setCounselorsList(counselors || []);
            setClients(clientData || []);
            setError(null);

            // Compute meeting count per client
            const counts = {};
            (allMeetings || []).forEach(m => {
                if (m.clientId) {
                    counts[m.clientId] = (counts[m.clientId] || 0) + 1;
                }
                if (m.clientEmail) {
                    const lowEmail = m.clientEmail.toLowerCase();
                    counts[lowEmail] = (counts[lowEmail] || 0) + 1;
                }
            });
            setMeetingCounts(counts);

            // Fetch pending public insights for this counselor
            try {
                const insights = await DataManager.getPublicInsightsForCounselor(user.id);
                setPendingInsights(insights || []);
                
                // Pre-populate selections based on email matches
                const initialSelections = {};
                (insights || []).forEach(insight => {
                    const matchedClient = (clientData || []).find(c => c.email?.toLowerCase() === insight.email?.toLowerCase());
                    if (matchedClient) {
                        initialSelections[insight.id] = matchedClient.id;
                    }
                });
                setInsightClientSelections(initialSelections);
            } catch (err) {
                console.error("Errore recupero insight pubblici:", err);
            }
        } catch (err) {
            console.error("Failed to load clients:", err);
            setError("Impossibile caricare i clienti. Controlla la connessione.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch clients on mount or when filter changes
    useEffect(() => {
        setNewClientForm(prev => ({ ...prev, counselorId: user.id }));
        fetchClients();
    }, [user.email, user.id, filterCounselor]);

    const openClient = (client) => {
        setSelectedClient(client);
        setView('detail');
    };

    // Supervision Access Requests pending for this counselor's clients
    const pendingSuperAdminRequests = useMemo(() => {
        return (clients || []).filter(c => {
            const isOwned = c.counselorId === user.id || c.counselorEmail === user.email || (!c.counselorId && !c.counselorEmail);
            return isOwned && c.superAdminAccessStatus === 'pending';
        });
    }, [clients, user.id, user.email]);

    const handleQuickApproveAccess = async (e, clientId) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        try {
            setLoading(true);
            await DataManager.approveSuperAdminAccess(clientId, {
                approvedBy: user.email,
                approvedByName: user.name || user.email
            });
            await fetchClients();
            alert("✅ Richiesta di supervisione approvata!");
        } catch (err) {
            console.error("Errore approvazione:", err);
            alert("Errore approvazione: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleQuickRejectAccess = async (e, clientId) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (confirm("Rifiutare la richiesta di accesso del SuperAdmin per questo cliente?")) {
            try {
                setLoading(true);
                await DataManager.rejectSuperAdminAccess(clientId, {
                    rejectedBy: user.email,
                    rejectedByName: user.name || user.email
                });
                await fetchClients();
            } catch (err) {
                console.error("Errore rifiuto:", err);
                alert("Errore rifiuto: " + err.message);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleQuickRevokeAccess = async (e, clientId) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (confirm("Revocare l'autorizzazione di visualizzazione al SuperAdmin per questo cliente? Le note degli incontri torneranno private ed esclusive per te.")) {
            try {
                setLoading(true);
                await DataManager.revokeSuperAdminAccess(clientId, {
                    revokedBy: user.email,
                    revokedByName: user.name || user.email
                });
                await fetchClients();
                alert("🔒 Accesso SuperAdmin revocato con successo.");
            } catch (err) {
                console.error("Errore revoca:", err);
                alert("Errore revoca: " + err.message);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleToggleClientActive = async (e, client) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const newActiveState = client.isActive === false ? true : false;
        const actionText = newActiveState ? "attivare" : "disattivare";
        if (confirm(`Vuoi ${actionText} il cliente "${client.name || client.email}"?`)) {
            try {
                setLoading(true);
                await DataManager.updateClient(client.id, { isActive: newActiveState });
                await fetchClients();
            } catch (err) {
                console.error("Errore modifica stato cliente:", err);
                alert("Errore modifica stato: " + err.message);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDeleteClient = async (e, clientId, clientName) => {
        e.preventDefault();
        e.stopPropagation();
        const displayName = clientName ? `"${clientName}"` : "questo cliente";
        if (confirm(`Sei sicuro di voler eliminare definitivamente ${displayName}? L'azione è irreversibile e cancellerà la cartella e tutti i dati associati.`)) {
            try {
                setLoading(true);
                await DataManager.deleteClient(clientId);
                await fetchClients();
            } catch (err) {
                alert("Errore durante l'eliminazione: " + err.message);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleCreateNewClient = async () => {
        const trimmedName = newClientForm.name.trim();
        const trimmedEmail = newClientForm.email?.trim().toLowerCase() || '';
        let clientMode = newClientForm.mode || 'manual';

        if (!trimmedName) {
            alert('Inserisci il nome del cliente.');
            return;
        }

        // Se l'email non è inserita, il cliente è sempre in modalità manuale (senza accesso app)
        if (!trimmedEmail) {
            clientMode = 'manual';
        } else if (!trimmedEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            alert('⚠️ Inserisci un indirizzo email valido (oppure lascia il campo vuoto se il cliente non deve accedere all\'app).');
            return;
        }

        if (clientMode === 'autonomous' && newClientForm.testPassword && newClientForm.testPassword.length < 6) {
            alert('La password di prova deve contenere almeno 6 caratteri.');
            return;
        }

        // Prevenzione duplicazione clienti: blocca solo se sia nome sia email coincidono con un cliente esistente
        if (trimmedEmail && clients.some(c => (c.email || '').toLowerCase().trim() === trimmedEmail && (c.name || '').toLowerCase().trim() === trimmedName.toLowerCase())) {
            alert(`⚠️ Esiste già un cliente registrato con questo nome e indirizzo email: ${trimmedEmail}.\n\nNon è consentito creare duplicati.`);
            return;
        }

        try {
            setLoading(true);
            const passwordToUse = (clientMode === 'autonomous') 
                ? (newClientForm.testPassword?.trim() || 'Awareness2026!') 
                : '';

            const payload = {
                name: trimmedName,
                email: trimmedEmail,
                mode: clientMode,
                counselorId: newClientForm.counselorId || user.id
            };
            if (passwordToUse) {
                payload.initialPassword = passwordToUse;
            }
            if (newClientForm.firebaseUid?.trim()) {
                payload.firebaseUid = newClientForm.firebaseUid.trim();
            }

            // Provision Firebase Auth account automatically ONLY if autonomous and email provided
            if (clientMode === 'autonomous' && trimmedEmail && passwordToUse) {
                try {
                    const authRes = await AuthService.createUserWithPassword(trimmedEmail, passwordToUse);
                    if (authRes.created && authRes.uid) {
                        payload.firebaseUid = authRes.uid;
                    } else if (authRes.reason === 'EMAIL_EXISTS') {
                        console.info("Firebase Auth account already exists, linked.");
                    }
                } catch (authErr) {
                    console.warn("Auth provision notice:", authErr.message);
                }
            }

            const newClient = await DataManager.createClient(payload);
            await fetchClients();
            setShowNewClientModal(false);
            
            setNewClientForm({ name: '', email: '', firebaseUid: '', counselorId: user.id, testPassword: '', mode: 'manual' });

            if (clientMode === 'autonomous' && trimmedEmail) {
                const successMsg = `✅ Cliente "${newClient.name}" registrato sia nel Database che in Firebase Auth!\n\n🔑 Credenziali di Accesso Create:\nEmail: ${trimmedEmail}\nPassword: ${passwordToUse}\n\nL'utente può accedere subito inserendo queste credenziali nel login cliente.`;
                alert(successMsg);
            } else {
                alert(`✅ Cartella cliente per "${newClient.name}" creata con successo (modalità Solo Counselor, nessun accesso app).`);
            }
        } catch (err) {
            console.error("Error creating client:", err);
            alert("Errore creazione cliente: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    // INBOX HANDLERS
    const handleAcceptInsight = async (insight) => {
        const selectedClientId = insightClientSelections[insight.id];
        if (!selectedClientId) {
            alert("Seleziona a quale cliente associare questo insight.");
            return;
        }
        try {
            setLoading(true);
            await DataManager.acceptPublicInsight(insight, selectedClientId);
            const updatedInsights = pendingInsights.filter(i => i.id !== insight.id);
            setPendingInsights(updatedInsights);
            if (updatedInsights.length === 0) setShowInboxModal(false);
        } catch (err) {
            alert("Errore accettazione insight: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRejectInsight = async (insightId) => {
        if (!confirm("Sei sicuro di voler scartare ed eliminare questo insight?")) return;
        try {
            setLoading(true);
            await DataManager.rejectPublicInsight(insightId);
            const updatedInsights = pendingInsights.filter(i => i.id !== insightId);
            setPendingInsights(updatedInsights);
            if (updatedInsights.length === 0) setShowInboxModal(false);
        } catch (err) {
            alert("Errore rifiuto insight: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Filter and sort clients for display
    const filteredClients = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        return [...clients]
            .filter(c => {
                if (!q) return true;
                const name = (c.name || '').toLowerCase();
                const email = (c.email || '').toLowerCase();
                const contract = (c.contract?.title || '').toLowerCase();
                return name.includes(q) || email.includes(q) || contract.includes(q);
            })
            .sort((a, b) => {
                // Status sort (Active first)
                const aActive = a.isActive !== false;
                const bActive = b.isActive !== false;
                if (aActive && !bActive) return -1;
                if (!aActive && bActive) return 1;
                // Name sort
                const nameA = a.name || a.id || '';
                const nameB = b.name || b.id || '';
                return nameA.localeCompare(nameB);
            });
    }, [clients, searchQuery]);

    if (view === 'detail' && selectedClient) {
        return (
            <ClientDetailView
                counselor={user}
                client={selectedClient}
                counselorsList={counselorsList}
                onBack={() => {
                    setSelectedClient(null);
                    setView('list');
                    fetchClients();
                }}
            />
        );
    }

    if (view === 'kb' && selectedKb) {
        return (
            <KnowledgeBaseView
                type={selectedKb}
                clients={clients}
                onBack={() => {
                    setView('list');
                    setSelectedKb(null);
                }}
            />
        );
    }

    if (view === 'reports') {
        return (
            <div className="min-h-screen bg-gray-50 animate-fade-in pb-20">
                <header className="bg-white p-4 sticky top-0 z-20 shadow-sm flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setView('list')} className="p-2 hover:bg-gray-100 rounded-full transition">←</button>
                        <h1 className="text-lg font-bold text-gray-800">Reportistica & Gestione Studio</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <img src="/logo.png" alt="Logo" className="h-8 object-contain" />
                        <span className="text-sm font-black tracking-tighter text-slate-800 uppercase">AwarenessTool</span>
                    </div>
                </header>
                <main className="p-6 max-w-6xl mx-auto">
                    <ReportsView
                        clients={clients}
                        onUpdateClient={(updatedClient) => {
                            setClients(prev => prev.map(c => c.id === updatedClient.id ? updatedClient : c));
                        }}
                        onOpenClient={openClient}
                    />
                </main>
            </div>
        );
    }

    if (view === 'toolkit') {
        return (
            <div className="min-h-screen bg-gray-50 animate-fade-in pb-20">
                <header className="bg-white p-4 sticky top-0 z-20 shadow-sm flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setView('list')} className="p-2 hover:bg-gray-100 rounded-full transition">←</button>
                        <h1 className="text-lg font-bold text-gray-800">Toolkit Strumenti</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <img src="/logo.png" alt="Logo" className="h-8 object-contain" />
                        <span className="text-sm font-black tracking-tighter text-slate-800 uppercase">AwarenessTool</span>
                    </div>
                </header>
                <main className="p-6 max-w-6xl mx-auto">
                    <ToolkitView clients={clients} counselor={user} counselorsList={counselorsList} />
                </main>
            </div>
        );
    }

    if (view === 'peopleCare') {
        if (!hasBuenaVistaAccess) {
            setView('list');
            return null;
        }
        return (
            <div className="min-h-screen bg-slate-50 animate-fade-in pb-20">
                <header className="bg-white p-4 sticky top-0 z-20 shadow-sm flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setView('list')} className="p-2 hover:bg-gray-100 rounded-full transition font-bold">← Torna alla Dashboard</button>
                        <h1 className="text-lg font-bold text-gray-800">People Care — BuenaVista Analytics</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        {isAdmin && (
                            <button
                                onClick={() => setShowOrgModal(true)}
                                className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition flex items-center gap-1.5"
                            >
                                <span>🏢</span> Anagrafica Enti
                            </button>
                        )}
                        <img src="/logo.png" alt="Logo" className="h-8 object-contain" />
                    </div>
                </header>
                <main className="p-4 md:p-8 max-w-7xl mx-auto">
                    <PeopleCareAnalyticsView
                        user={user}
                        counselorsList={counselorsList}
                        onBack={() => setView('list')}
                        onOpenClient={openClient}
                    />
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 animate-fade-in pb-20 overflow-x-hidden w-full">
            <header className="bg-white px-4 py-3 md:px-8 md:py-4 sticky top-0 z-20 shadow-sm border-b border-gray-100 flex justify-between items-center">
                {/* BRAND LOGO */}
                <div className="flex items-center gap-2 md:gap-3">
                    <img src="/logo.png" alt="AwarenessTool Logo" className="h-7 md:h-9 object-contain drop-shadow-sm" />
                    <span className="text-xs md:text-sm font-black tracking-tighter text-slate-800 uppercase">AwarenessTool</span>
                    <span className="hidden md:block bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-widest uppercase ml-2">
                        Studio
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setMenuOpen(!menuOpen)}
                        className="h-10 w-10 bg-slate-900 text-white rounded-full flex items-center justify-center font-bold hover:bg-slate-800 transition shadow-sm"
                    >
                        {user.name ? user.name[0] : 'C'}
                    </button>
                    {menuOpen && (
                        <div className="absolute right-4 top-14 w-60 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-30 animate-fade-in">
                            <div className="px-4 py-3 bg-slate-50 text-xs text-center text-slate-600 font-bold border-b border-slate-100">
                                {isAdmin ? '👑 SUPER ADMIN' : user.email}
                            </div>
                            
                            {isAdmin && (
                                <>
                                    <button
                                        onClick={() => {
                                            setShowCounselorModal(true);
                                            setMenuOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2.5 text-sm text-blue-700 bg-blue-50/50 hover:bg-blue-100/60 font-bold flex items-center gap-2 transition"
                                    >
                                        👥 Gestione Counselor
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowAccessLogModal(true);
                                            setMenuOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2.5 text-sm text-teal-700 bg-teal-50/50 hover:bg-teal-100/60 font-bold flex items-center gap-2 transition"
                                    >
                                        🛡️ Log Supervisione & Grant
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowOrgModal(true);
                                            setMenuOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2.5 text-sm text-teal-800 bg-teal-50/70 hover:bg-teal-100 font-bold flex items-center gap-2 transition"
                                    >
                                        🏢 Anagrafica Enti (BuenaVista)
                                    </button>
                                </>
                            )}

                            {hasBuenaVistaAccess && (
                                <button
                                    onClick={() => {
                                        setView('peopleCare');
                                        setMenuOpen(false);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-teal-900 bg-gradient-to-r from-teal-50 to-emerald-50 hover:from-teal-100 hover:to-emerald-100 flex items-center gap-2 font-bold transition border-y border-teal-100/60"
                                >
                                    🏢 People Care — BuenaVista Analytics
                                </button>
                            )}

                            <button
                                onClick={() => {
                                    setView('toolkit');
                                    setMenuOpen(false);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 font-medium"
                            >
                                🧰 Toolkit Strumenti
                            </button>
                            <button
                                onClick={() => {
                                    setShowGroupModal(true);
                                    setMenuOpen(false);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 font-medium"
                            >
                                👥 Counseling di Gruppo
                            </button>
                            <button
                                onClick={() => {
                                    setView('reports');
                                    setMenuOpen(false);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 font-medium"
                            >
                                📊 Reportistica
                            </button>
                            <button
                                onClick={() => {
                                    setShowInboxModal(true);
                                    setMenuOpen(false);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 font-medium"
                            >
                                📥 Inbox Insight {pendingInsights.length > 0 && `(${pendingInsights.length})`}
                            </button>
                            <div className="h-px bg-gray-100 my-1"></div>
                            <button
                                onClick={() => {
                                    setShowPrivacyModal(true);
                                    setMenuOpen(false);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-indigo-700 bg-indigo-50/50 hover:bg-indigo-100/60 flex items-center gap-2 font-bold transition"
                            >
                                🛡️ Informativa Privacy & GDPR
                            </button>
                            <button
                                onClick={() => {
                                    setShowSettings(true);
                                    setMenuOpen(false);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                                ⚙️ Impostazioni Studio
                            </button>
                            <div className="h-px bg-gray-100 my-1"></div>
                            <button onClick={onLogout} className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 font-bold flex items-center gap-2">
                                🚪 Esci
                            </button>
                        </div>
                    )}
                </div>
            </header>

            <main className="p-4 md:p-8 max-w-6xl mx-auto space-y-8">
                {/* 1. WELCOME HERO & QUICK METRICS */}
                <div 
                    className="rounded-3xl p-6 md:p-8 shadow-md relative overflow-hidden text-white"
                    style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
                >
                    <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                        <div>
                            <div className="flex items-center gap-2 mb-2.5">
                                <span 
                                    className="text-[10px] md:text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1.5 border"
                                    style={{ backgroundColor: 'rgba(99, 102, 241, 0.25)', color: '#e0e7ff', borderColor: '#818cf8' }}
                                >
                                    <span>{isAdmin ? '👑' : '🧘'}</span>
                                    <span>{isAdmin ? 'Super Admin Console' : 'Counselor Studio'}</span>
                                </span>
                            </div>
                            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">
                                Ciao, {user.name || user.email?.split('@')[0]} 👋
                            </h1>
                            <p className="text-slate-300 text-xs md:text-sm mt-1.5 max-w-xl font-medium leading-relaxed">
                                Benvenuto nel tuo hub operativo. Gestisci i tuoi strumenti di counseling, i gruppi, i report e i clienti attivi.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {pendingInsights.length > 0 && (
                                <button
                                    onClick={() => setShowInboxModal(true)}
                                    className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl font-bold shadow-md transition flex items-center gap-2 text-xs md:text-sm animate-pulse"
                                >
                                    <span>📥</span> Inbox Insight ({pendingInsights.length})
                                </button>
                            )}
                            <button
                                onClick={() => setShowNewClientModal(true)}
                                className="px-5 py-2.5 bg-white text-slate-900 hover:bg-slate-100 rounded-2xl font-bold shadow-md transition flex items-center gap-2 text-xs md:text-sm active:scale-95"
                            >
                                <span>➕</span> Nuovo Cliente
                            </button>
                        </div>
                    </div>
                </div>

                {/* PENDING SUPERADMIN ACCESS REQUESTS BANNER FOR COUNSELORS */}
                {pendingSuperAdminRequests.length > 0 && (
                    <div className="bg-amber-50/90 border-2 border-amber-300 rounded-3xl p-5 md:p-6 shadow-sm text-slate-900 animate-fade-in">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="text-3xl">🔔</span>
                            <div>
                                <h3 className="text-base md:text-lg font-black text-amber-950 tracking-tight">
                                    Richieste di Supervisione in Sospeso ({pendingSuperAdminRequests.length})
                                </h3>
                                <p className="text-xs md:text-sm text-amber-900 font-medium">
                                    Il SuperAdmin ha richiesto l'autorizzazione a consultare gli incontri dei seguenti clienti.
                                </p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {pendingSuperAdminRequests.map(reqClient => (
                                <div 
                                    key={reqClient.id}
                                    className="bg-white border border-amber-200/90 rounded-2xl p-4 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                                >
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-extrabold text-sm md:text-base text-slate-900">{reqClient.name}</span>
                                            <span className="text-xs bg-slate-100 text-slate-700 px-2.5 py-0.5 rounded-full font-semibold border border-slate-200">
                                                {reqClient.email}
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-600">
                                            Richiesto da: <span className="font-bold text-slate-900">{reqClient.superAdminAccessRequestedByName || reqClient.superAdminAccessRequestedBy || 'SuperAdmin'}</span>
                                            {reqClient.superAdminAccessNotes && (
                                                <span className="italic ml-2 text-slate-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 inline-block mt-1 sm:mt-0">
                                                    «{reqClient.superAdminAccessNotes}»
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 self-end sm:self-auto flex-shrink-0">
                                        <button
                                            onClick={() => openClient(reqClient)}
                                            className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 rounded-xl text-xs font-bold transition shadow-xs"
                                        >
                                            📂 Apri Cartella
                                        </button>
                                        <button
                                            onClick={(e) => handleQuickApproveAccess(e, reqClient.id)}
                                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition shadow-xs flex items-center gap-1.5"
                                        >
                                            <span>✓</span> Approva
                                        </button>
                                        <button
                                            onClick={(e) => handleQuickRejectAccess(e, reqClient.id)}
                                            className="px-3.5 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold transition"
                                        >
                                            ✕ Rifiuta
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 2. HOMEPAGE QUICK HUB MODULES (Mirroring Burger Menu) */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-slate-800">Strumenti & Moduli Rapidi</h2>
                            <p className="text-xs text-slate-600 font-medium">Accesso diretto alle sezioni professionali dello studio</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 md:gap-4">
                        {/* Tile 1: Toolkit */}
                        <button
                            onClick={() => setView('toolkit')}
                            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-400 transition text-left group flex flex-col justify-between h-36"
                        >
                            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center text-xl group-hover:scale-110 transition font-bold">
                                🧰
                            </div>
                            <div>
                                <h3 className="text-xs md:text-sm font-bold text-slate-800 group-hover:text-indigo-600 transition">Toolkit Strumenti</h3>
                                <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 font-medium">Ruota, Baseline, Carte, Grounding</p>
                            </div>
                        </button>

                        {/* Tile 2: Group Counseling */}
                        <button
                            onClick={() => setShowGroupModal(true)}
                            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-emerald-400 transition text-left group flex flex-col justify-between h-36"
                        >
                            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center text-xl group-hover:scale-110 transition font-bold">
                                👥
                            </div>
                            <div>
                                <h3 className="text-xs md:text-sm font-bold text-slate-800 group-hover:text-emerald-600 transition">Counseling Gruppo</h3>
                                <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 font-medium">Minireport, dinamiche, tecniche</p>
                            </div>
                        </button>

                        {/* Tile 3: Reports */}
                        <button
                            onClick={() => setView('reports')}
                            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-purple-400 transition text-left group flex flex-col justify-between h-36"
                        >
                            <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center text-xl group-hover:scale-110 transition font-bold">
                                📊
                            </div>
                            <div>
                                <h3 className="text-xs md:text-sm font-bold text-slate-800 group-hover:text-purple-600 transition">Reportistica</h3>
                                <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 font-medium">Sintesi cliniche ed evoluzioni</p>
                            </div>
                        </button>

                        {/* Tile 4: SuperAdmin Counselor Management */}
                        {isAdmin ? (
                            <button
                                onClick={() => setShowCounselorModal(true)}
                                className="bg-white p-4 rounded-2xl border border-blue-200 shadow-sm hover:shadow-md hover:border-blue-400 transition text-left group flex flex-col justify-between h-36 relative overflow-hidden"
                            >
                                <span className="absolute top-2 right-2 bg-blue-100 text-blue-800 text-[8px] font-black px-1.5 py-0.5 rounded border border-blue-200">ADMIN</span>
                                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center text-xl group-hover:scale-110 transition font-bold">
                                    👑
                                </div>
                                <div>
                                    <h3 className="text-xs md:text-sm font-bold text-blue-900 group-hover:text-blue-700 transition">Gestione Counselor</h3>
                                    <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 font-medium">Ruoli, accessi e password</p>
                                </div>
                            </button>
                        ) : (
                            <button
                                onClick={() => setShowSettings(true)}
                                className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-400 transition text-left group flex flex-col justify-between h-36"
                            >
                                <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-800 flex items-center justify-center text-xl group-hover:scale-110 transition font-bold">
                                    ⚙️
                                </div>
                                <div>
                                    <h3 className="text-xs md:text-sm font-bold text-slate-800 group-hover:text-slate-900 transition">Impostazioni</h3>
                                    <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 font-medium">Profilo e preferenze</p>
                                </div>
                            </button>
                        )}

                        {/* Tile 5: SuperAdmin Access & Grant Log (SuperAdmin Only) */}
                        {isAdmin && (
                            <button
                                onClick={() => setShowAccessLogModal(true)}
                                className="bg-white p-4 rounded-2xl border border-teal-200 shadow-sm hover:shadow-md hover:border-teal-400 transition text-left group flex flex-col justify-between h-36 relative overflow-hidden"
                            >
                                <span className="absolute top-2 right-2 bg-teal-100 text-teal-800 text-[8px] font-black px-1.5 py-0.5 rounded border border-teal-200">LOG</span>
                                <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center text-xl group-hover:scale-110 transition font-bold">
                                    🛡️
                                </div>
                                <div>
                                    <h3 className="text-xs md:text-sm font-bold text-teal-900 group-hover:text-teal-700 transition">Log Supervisione</h3>
                                    <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 font-medium">Audit richieste e grant</p>
                                </div>
                            </button>
                        )}

                        {/* Tile 6: Inbox */}
                        <button
                            onClick={() => setShowInboxModal(true)}
                            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-amber-400 transition text-left group flex flex-col justify-between h-36 relative"
                        >
                            {pendingInsights.length > 0 && (
                                <span className="absolute top-2 right-2 bg-red-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full animate-pulse">
                                    {pendingInsights.length}
                                </span>
                            )}
                            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center text-xl group-hover:scale-110 transition font-bold">
                                📥
                            </div>
                            <div>
                                <h3 className="text-xs md:text-sm font-bold text-slate-800 group-hover:text-amber-600 transition">Inbox Insight</h3>
                                <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 font-medium">{pendingInsights.length} in attesa</p>
                            </div>
                        </button>

                        {/* Tile 7: People Care BuenaVista Analytics (Visible ONLY to BuenaVista members/supervisors) */}
                        {hasBuenaVistaAccess && (
                            <button
                                onClick={() => setView('peopleCare')}
                                className="bg-white p-4 rounded-2xl border border-indigo-200 shadow-sm hover:shadow-md hover:border-indigo-400 transition text-left group flex flex-col justify-between h-36 relative overflow-hidden"
                            >
                                <span className="absolute top-2 right-2 bg-indigo-100 text-indigo-800 text-[8px] font-black px-1.5 py-0.5 rounded border border-indigo-200">PEOPLE CARE</span>
                                <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center text-xl group-hover:scale-110 transition font-bold">
                                    🏢
                                </div>
                                <div>
                                    <h3 className="text-xs md:text-sm font-bold text-indigo-950 group-hover:text-indigo-600 transition">People Care Analytics</h3>
                                    <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 font-medium">BuenaVista: IN vs OUT, aree e NPS</p>
                                </div>
                            </button>
                        )}

                        {/* Tile 8: Anagrafica Enti (Admin / Counselor) */}
                        {isAdmin && (
                            <button
                                onClick={() => setShowOrgModal(true)}
                                className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-400 transition text-left group flex flex-col justify-between h-36"
                            >
                                <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-800 flex items-center justify-center text-xl group-hover:scale-110 transition font-bold">
                                    🏛️
                                </div>
                                <div>
                                    <h3 className="text-xs md:text-sm font-bold text-slate-800 group-hover:text-slate-900 transition">Anagrafica Enti</h3>
                                    <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 font-medium">Organizzazioni partner (BuenaVista)</p>
                                </div>
                            </button>
                        )}

                        {/* Tile 9: Cloud & Backup */}
                        <button
                            onClick={() => setShowSettings(true)}
                            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-teal-400 transition text-left group flex flex-col justify-between h-36"
                        >
                            <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center text-xl group-hover:scale-110 transition font-bold">
                                ☁️
                            </div>
                            <div>
                                <h3 className="text-xs md:text-sm font-bold text-slate-800 group-hover:text-teal-600 transition">Cloud & Backup</h3>
                                <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 font-medium">Stato sincrono & export dati</p>
                            </div>
                        </button>

                        {/* Tile 10: Informativa Privacy & GDPR */}
                        <button
                            onClick={() => setShowPrivacyModal(true)}
                            className="bg-white p-4 rounded-2xl border border-indigo-200 shadow-sm hover:shadow-md hover:border-indigo-400 transition text-left group flex flex-col justify-between h-36 relative overflow-hidden cursor-pointer"
                        >
                            <span className="absolute top-2 right-2 bg-indigo-100 text-indigo-800 text-[8px] font-black px-1.5 py-0.5 rounded border border-indigo-200">GDPR 2016/679</span>
                            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center text-xl group-hover:scale-110 transition font-bold">
                                🛡️
                            </div>
                            <div>
                                <h3 className="text-xs md:text-sm font-bold text-slate-800 group-hover:text-indigo-600 transition">Informativa Privacy</h3>
                                <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 font-medium">Trasparenza dati clinici e People Care</p>
                            </div>
                        </button>
                    </div>
                </div>

                {/* 3. CLIENT MANAGEMENT SECTION WITH REALTIME SEARCH & COUNSELOR FILTER */}
                <div>
                    <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-4">
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-slate-800 flex items-center gap-2">
                                <span>👤</span> Cartelle Clienti ({filteredClients.length})
                            </h2>
                            <p className="text-xs text-slate-500">
                                {isAdmin ? 'Visualizzazione e supervisione clienti (sola lettura per clienti di altri counselor)' : 'I tuoi clienti assegnati in carico'}
                            </p>
                        </div>
                        
                        {/* Search & SuperAdmin filter bar */}
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="relative flex-1 sm:w-64">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Cerca cliente per nome, email..."
                                    className="w-full pl-9 pr-8 py-2 text-xs bg-white border border-slate-200 rounded-xl focus:border-indigo-500 focus:outline-none transition shadow-sm font-medium text-slate-800"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-bold p-1"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>

                            {isAdmin && counselorsList.length > 0 && (
                                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-sm">
                                    <span className="text-[11px] font-bold text-slate-500">Counselor:</span>
                                    <select
                                        value={filterCounselor}
                                        onChange={(e) => setFilterCounselor(e.target.value)}
                                        className="text-xs font-bold text-slate-800 bg-transparent outline-none cursor-pointer"
                                    >
                                        <option value="all">Tutti i Counselor</option>
                                        {counselorsList.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.name || c.email}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center h-64 bg-white rounded-3xl border border-slate-100 shadow-sm">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                            <span className="ml-3 text-sm text-gray-500 font-medium">Caricamento clienti...</span>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl">
                            {error}
                        </div>
                    ) : filteredClients.length === 0 ? (
                        <div className="p-12 text-center bg-white rounded-3xl border border-slate-100 shadow-sm">
                            <span className="text-4xl block mb-2">🔍</span>
                            <p className="text-gray-500 font-medium">
                                {searchQuery ? 'Nessun cliente trovato per i criteri di ricerca inseriti.' : 'Nessun cliente assegnato.'}
                            </p>
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="mt-3 text-xs text-indigo-600 font-bold hover:underline"
                                >
                                    Cancella ricerca
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-4">
                            {filteredClients.map(c => {
                                const isActive = c.isActive !== false;
                                const isForeignClient = isSupervisor && c.counselorId && c.counselorId !== user.id && c.counselorEmail !== user.email;

                                return (
                                    <div
                                        key={c.id}
                                        onClick={() => openClient(c)}
                                        className={`bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition cursor-pointer border-2 relative group ${isActive ? 'border-transparent hover:border-indigo-100' : 'border-gray-100 opacity-75 grayscale-[0.5]'
                                            }`}
                                    >
                                        {(isAdmin || !isForeignClient) && (
                                            <button
                                                onClick={(e) => handleDeleteClient(e, c.id, c.name)}
                                                className="absolute top-4 right-4 w-7 h-7 rounded-full bg-slate-100/90 hover:bg-rose-50 text-slate-400 hover:text-rose-600 border border-slate-200 hover:border-rose-300 flex items-center justify-center transition-all opacity-40 group-hover:opacity-100 z-10 shadow-2xs text-xs"
                                                title={isAdmin && isForeignClient ? "Elimina Cliente (Super Admin)" : "Elimina Cliente"}
                                            >
                                                🗑️
                                            </button>
                                        )}
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex flex-col">
                                                <h3 className={`text-lg font-bold text-slate-800 ${!isActive ? 'text-gray-500' : ''}`}>{c.name || 'Cliente senza nome'}</h3>
                                                <div className="text-xs text-indigo-700 font-semibold mt-1 flex items-center gap-1.5 flex-wrap">
                                                    <span className="text-slate-400 font-medium">🧑‍💼 Counselor:</span>
                                                    <span className="font-bold text-slate-800">
                                                        {(() => {
                                                            const rawEmail = (c.counselorEmail || '').toLowerCase().trim();
                                                            const rawId = (c.counselorId || '').toLowerCase().trim();
                                                            if (rawEmail === 'martina@gmail.com' || rawId === 'martina_vaccarone_counselor' || rawId === 'martina_tavarone_counselor') return 'Martina Vaccarone';
                                                            if (rawEmail === 'trebino.martina@gmail.com' || rawId === 'martina_trebino_counselor') return 'Martina Trebino';

                                                            const cObj = counselorsList.find(counselor => counselor.id === c.counselorId || counselor.email === c.counselorEmail);
                                                            if (cObj?.email?.toLowerCase() === 'martina@gmail.com') return 'Martina Vaccarone';
                                                            if (cObj?.email?.toLowerCase() === 'trebino.martina@gmail.com') return 'Martina Trebino';
                                                            return cObj?.name || c.counselorName || c.counselorEmail || (c.counselorId ? `ID: ${c.counselorId.substring(0, 8)}...` : 'Non specificato');
                                                        })()}
                                                    </span>
                                                    {isForeignClient && (
                                                        <>
                                                            {(() => {
                                                                const isClientBuenaVista = Boolean(
                                                                    c.organization === 'buenavista' || 
                                                                    (c.organizationName || '').toLowerCase().includes('buenavista') || 
                                                                    c.settingType === 'organizzativo'
                                                                );
                                                                if (isClientBuenaVista) {
                                                                    return (
                                                                        <span className="text-[9px] bg-teal-100 text-teal-800 px-2 py-0.5 rounded-full font-bold ml-1 flex items-center gap-1">
                                                                            <span>🏢</span> BuenaVista (Supervisione)
                                                                        </span>
                                                                    );
                                                                }
                                                                return (
                                                                    <>
                                                                        <span className="text-[9px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-black uppercase tracking-wider ml-1">
                                                                            Supervisione
                                                                        </span>
                                                                        {c.superAdminAccessStatus === 'approved' ? (
                                                                            <span className="text-[9px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold ml-0.5">
                                                                                🛡️ Incontri: Autorizzati
                                                                            </span>
                                                                        ) : c.superAdminAccessStatus === 'pending' ? (
                                                                            <span className="text-[9px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold ml-0.5 animate-pulse">
                                                                                ⏳ Incontri: In Attesa
                                                                            </span>
                                                                        ) : c.superAdminAccessStatus === 'rejected' ? (
                                                                            <span className="text-[9px] bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full font-bold ml-0.5">
                                                                                🚫 Incontri: Respinto
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-[9px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-bold ml-0.5">
                                                                                🔒 Incontri: Da Richiedere
                                                                            </span>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </>
                                                    )}
                                                    {!isForeignClient && c.superAdminAccessStatus === 'pending' && (
                                                        <span className="text-[10px] bg-amber-50 text-amber-900 border border-amber-300 px-2 py-0.5 rounded-md font-bold flex items-center gap-1.5 ml-1">
                                                            <span>🔔 Richiesta SuperAdmin</span>
                                                            <button
                                                                onClick={(e) => handleQuickApproveAccess(e, c.id)}
                                                                className="text-[9px] bg-emerald-600 hover:bg-emerald-700 text-white px-1.5 py-0.5 rounded font-bold transition ml-0.5"
                                                                title="Approva richiesta SuperAdmin"
                                                            >
                                                                ✓
                                                            </button>
                                                            <button
                                                                onClick={(e) => handleQuickRejectAccess(e, c.id)}
                                                                className="text-[9px] bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded font-bold transition"
                                                                title="Rifiuta richiesta SuperAdmin"
                                                            >
                                                                ✕
                                                            </button>
                                                        </span>
                                                    )}
                                                    {!isForeignClient && c.superAdminAccessStatus === 'approved' && (
                                                        <span className="text-[10px] bg-emerald-50 text-emerald-900 border border-emerald-300 px-2 py-0.5 rounded-md font-bold flex items-center gap-1.5 ml-1">
                                                            <span>🛡️ SuperAdmin Autorizzato</span>
                                                            <button
                                                                onClick={(e) => handleQuickRevokeAccess(e, c.id)}
                                                                className="text-[9px] bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded font-bold transition ml-0.5"
                                                                title="Rimuovi / Revoca l'accesso del SuperAdmin per questo cliente"
                                                            >
                                                                🔒 Rimuovi
                                                            </button>
                                                        </span>
                                                    )}
                                                    {Boolean(isAdmin || (isOrgAdmin && (c.organization === 'buenavista' || (c.organizationName || '').toLowerCase().includes('buenavista')))) && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setClientToReassign(c);
                                                                setQuickReassignTargetCounselorId('');
                                                            }}
                                                            className="text-[10px] text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-2 py-0.5 rounded-md font-bold transition ml-1 cursor-pointer active:scale-95"
                                                            title={`Riassegna ${c.name || 'questo cliente'} ad un altro counselor`}
                                                        >
                                                            🔄 Riassegna
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {isActive ? (
                                                <button
                                                    onClick={(e) => handleToggleClientActive(e, c)}
                                                    className="bg-emerald-100 hover:bg-emerald-200 text-emerald-900 border border-emerald-300 text-[10px] px-2.5 py-1 rounded-full font-bold transition flex items-center gap-1.5 shadow-xs cursor-pointer active:scale-95"
                                                    title="Cliente Attivo — Clicca per disattivare"
                                                >
                                                    <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                                                    <span>Attivo</span>
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={(e) => handleToggleClientActive(e, c)}
                                                    className="bg-amber-100 hover:bg-emerald-100 text-amber-950 hover:text-emerald-900 border-2 border-amber-300 hover:border-emerald-400 text-[10px] px-2.5 py-1 rounded-full font-extrabold transition flex items-center gap-1.5 shadow-xs cursor-pointer animate-pulse active:scale-95"
                                                    title="Cliente Inattivo — Clicca qui per renderlo Attivo!"
                                                >
                                                    <span className="w-2 h-2 rounded-full bg-amber-600"></span>
                                                    <span>⚡ Rendi Attivo</span>
                                                </button>
                                            )}
                                        </div>
                                        
                                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                            <p className="text-slate-500 text-xs font-medium truncate">{c.email}</p>
                                            {(() => {
                                                const count = (meetingCounts[c.id] || 0) + 
                                                    (c.email && c.email.toLowerCase() !== c.id ? (meetingCounts[c.email.toLowerCase()] || 0) : 0) ||
                                                    (Array.isArray(c.meetings) ? c.meetings.length : 0);
                                                
                                                return (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 border border-indigo-200 text-indigo-950 shadow-2xs">
                                                        <span>🛋️</span>
                                                        <span><strong>{count}</strong> {count === 1 ? 'incontro' : 'incontri'}</span>
                                                    </span>
                                                );
                                            })()}
                                        </div>

                                        {c.nextMeetingDate && (
                                            <div className="mb-4 p-3 bg-indigo-50/70 border border-indigo-100 rounded-2xl flex items-center gap-3">
                                                <span className="text-xl">🗓️</span>
                                                <div>
                                                    <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest leading-none mb-1">Prossimo Incontro</p>
                                                    <p className="text-xs font-black text-indigo-700">
                                                        {new Date(c.nextMeetingDate).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                        <div className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Contratto di Counseling</div>
                                        <p className="text-xs text-gray-700 font-medium line-clamp-2">{c.contract?.title || 'Non definito'}</p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </main>

            {/* Settings Modal */}
            {showSettings && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center rounded-t-3xl">
                            <h2 className="text-xl font-bold text-gray-800">⚙️ Impostazioni Studio</h2>
                            <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">✕</button>
                        </div>
                        <div className="p-6 space-y-6">
                            <DataBackupManager />
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Nuovo Cliente */}
            {showNewClientModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-gray-800">➕ Nuovo Cliente</h3>
                            <button onClick={() => setShowNewClientModal(false)} className="text-gray-400 hover:text-gray-600 font-bold">✕</button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">Nome Completo</label>
                                <input
                                    type="text"
                                    placeholder="Mario Rossi"
                                    value={newClientForm.name}
                                    onChange={(e) => setNewClientForm({ ...newClientForm, name: e.target.value })}
                                    className="w-full p-3 border rounded-xl focus:border-brand-500 focus:outline-none"
                                />
                            </div>
                            {/* Scelta Modalità Accesso Piattaforma */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                                    Accesso alla Piattaforma
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setNewClientForm({ ...newClientForm, mode: 'manual' })}
                                        className={`p-3 rounded-2xl border-2 text-left transition ${
                                            (newClientForm.mode || 'manual') === 'manual'
                                                ? 'border-amber-500 bg-amber-50/70 text-amber-950 font-bold'
                                                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className="text-xs font-bold flex items-center gap-1.5">
                                            <span>🔒</span> Nessun Accesso
                                        </div>
                                        <p className="text-[10px] text-slate-500 font-normal mt-0.5">
                                            Solo counselor, nessuna email richiesta
                                        </p>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setNewClientForm({ ...newClientForm, mode: 'autonomous' })}
                                        className={`p-3 rounded-2xl border-2 text-left transition ${
                                            newClientForm.mode === 'autonomous'
                                                ? 'border-indigo-600 bg-indigo-50/70 text-indigo-950 font-bold'
                                                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className="text-xs font-bold flex items-center gap-1.5">
                                            <span>🟢</span> Con Accesso App
                                        </div>
                                        <p className="text-[10px] text-slate-500 font-normal mt-0.5">
                                            Il cliente accede per compilare il Diario
                                        </p>
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">
                                    Email {newClientForm.mode === 'autonomous' ? '(per accesso app)' : '(Opzionale)'}
                                </label>
                                <input
                                    type="email"
                                    autoComplete="off"
                                    placeholder={newClientForm.mode === 'autonomous' ? 'nome@esempio.it' : 'Opzionale (lascia vuoto se non accede)'}
                                    value={newClientForm.email}
                                    onChange={(e) => setNewClientForm({ ...newClientForm, email: e.target.value })}
                                    className="w-full p-3 border rounded-xl focus:border-brand-500 focus:outline-none"
                                />
                                <p className="text-[10px] text-gray-400 mt-1">
                                    {newClientForm.mode === 'autonomous' 
                                        ? 'Richiesta per consentire al cliente di effettuare il login.' 
                                        : 'Non obbligatoria. Se il cliente non accede all\'app, lascia pure vuoto questo campo.'}
                                </p>
                            </div>

                            {/* Counselor selector if Super Admin */}
                            {isAdmin && counselorsList.length > 0 && (
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Assegna a Counselor</label>
                                    <select
                                        value={newClientForm.counselorId}
                                        onChange={(e) => setNewClientForm({ ...newClientForm, counselorId: e.target.value })}
                                        className="w-full p-3 border rounded-xl focus:border-brand-500 focus:outline-none font-medium bg-white"
                                    >
                                        <option value={user.id}>{user.name || user.email} (Tu)</option>
                                        {counselorsList.filter(c => c.id !== user.id).map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.name || c.email}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {newClientForm.mode !== 'manual' ? (
                                <div>
                                    <label className="block text-xs font-bold text-amber-700 mb-1">🔑 Password di Prova / Accesso</label>
                                    <input
                                        type="text"
                                        placeholder="Awareness2026!"
                                        value={newClientForm.testPassword}
                                        onChange={(e) => setNewClientForm({ ...newClientForm, testPassword: e.target.value })}
                                        className="w-full p-3 border-2 border-amber-200 rounded-xl focus:border-amber-500 focus:outline-none bg-amber-50/40 font-mono text-sm"
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">
                                        Crea automaticamente l'account in Firebase Auth consentendo al cliente di accedere subito.
                                    </p>
                                </div>
                            ) : (
                                <div className="p-3 bg-amber-50 rounded-2xl border border-amber-200 text-xs text-amber-800">
                                    ℹ️ In modalità <strong>Nessun Accesso</strong>, il cliente non riceverà credenziali, non accederà all'app e la sezione Diario sarà disabilitata. Tutte le note e gli incontri saranno gestiti direttamente dal counselor.
                                </div>
                            )}

                            <div className="flex justify-end gap-2 pt-4">
                                <button onClick={() => setShowNewClientModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Annulla</button>
                                <Button onClick={handleCreateNewClient} disabled={loading}>Crea Cliente</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Inbox Insight Pubblici */}
            {showInboxModal && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fade-in backdrop-blur-sm">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden border border-slate-100">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-slate-50">
                            <div>
                                <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                                    <span>📥</span> Inbox Insight Pubblici
                                </h3>
                                <p className="text-xs text-slate-500">Compilazioni ricevute tramite il form esterno</p>
                            </div>
                            <button onClick={() => setShowInboxModal(false)} className="text-gray-400 hover:text-gray-600 font-bold text-xl">✕</button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto space-y-5 flex-1">
                            {/* EXPLANATORY HERO / GUIDE */}
                            <div className="p-4 bg-indigo-50/80 rounded-2xl border border-indigo-100 text-xs space-y-2.5">
                                <div className="flex items-center gap-2 font-bold text-indigo-950 text-sm">
                                    <span>💡</span> Come arrivano le risposte qui dentro?
                                </div>
                                <p className="text-slate-700 leading-relaxed font-medium">
                                    I clienti o potenziali clienti possono compilare il <strong>Modulo di Insight Pubblico</strong> direttamente dal web (senza bisogno di login o credenziali). Puoi inviare loro il link qui sotto: non appena completano il questionario, le risposte compaiono in tempo reale in questa inbox per consentirti di associarle con un click a una cartella cliente.
                                </p>
                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
                                    <div className="flex-1 bg-white border border-indigo-200 rounded-xl px-3 py-2 font-mono text-[11px] text-indigo-900 truncate shadow-2xs select-all">
                                        {`${window.location.origin}/insight?counselor=${user.id || user.email}`}
                                    </div>
                                    <button
                                        onClick={() => {
                                            const url = `${window.location.origin}/insight?counselor=${user.id || user.email}`;
                                            navigator.clipboard.writeText(url);
                                            setCopiedPublicLink(true);
                                            setTimeout(() => setCopiedPublicLink(false), 2500);
                                        }}
                                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs transition flex items-center justify-center gap-1.5 whitespace-nowrap shadow-xs active:scale-95"
                                    >
                                        <span>{copiedPublicLink ? '✓ Link Copiato!' : '📋 Copia Link Modulo'}</span>
                                    </button>
                                </div>
                            </div>

                            {pendingInsights.length === 0 ? (
                                <div className="text-center py-10 bg-slate-50/60 rounded-2xl border border-dashed border-slate-200 text-slate-400 font-medium space-y-1">
                                    <span className="text-3xl block mb-1">📬</span>
                                    <p className="font-bold text-slate-700">Nessun insight in attesa al momento</p>
                                    <p className="text-xs text-slate-500">Condividi il link sopra per ricevere questionari e riflessioni spontanee.</p>
                                </div>
                            ) : pendingInsights.map(insight => {
                                const authorName = insight.name || insight.clientName || 'Anonimo';
                                const previewSnippet = (insight.notes || '').length > 140 
                                    ? (insight.notes || '').substring(0, 140) + '...' 
                                    : (insight.notes || '');

                                return (
                                    <div key={insight.id} className="p-5 border border-slate-200 rounded-2xl bg-white shadow-xs hover:shadow-md transition-shadow space-y-3">
                                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-[10px] font-black uppercase tracking-wider text-brand-700 bg-brand-100 px-2 py-0.5 rounded-full">
                                                        {insight.type || 'Riflessione Pubblica'}
                                                    </span>
                                                    {insight.name && (
                                                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                                                            ✓ Con Nome
                                                        </span>
                                                    )}
                                                </div>
                                                <h4 className="font-bold text-slate-900 text-base flex items-center gap-2">
                                                    <span>👤</span> {authorName}
                                                </h4>
                                                <p className="text-xs text-slate-500 mt-0.5 font-medium">
                                                    ✉️ {insight.email} • 🕒 {new Date(insight.submittedAt || insight.timestamp || Date.now()).toLocaleString('it-IT')}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => setSelectedInsightPreview(insight)}
                                                className="px-3.5 py-1.5 bg-slate-100 hover:bg-brand-50 hover:text-brand-700 text-slate-700 border border-slate-200 hover:border-brand-200 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 self-start shadow-2xs"
                                            >
                                                <span>👁️</span> Leggi / Anteprima
                                            </button>
                                        </div>

                                        {previewSnippet && (
                                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-xs text-slate-700 font-normal leading-relaxed italic line-clamp-2">
                                                "{previewSnippet}"
                                            </div>
                                        )}

                                        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                                            <span className="text-xs font-bold text-slate-700">Associa a:</span>
                                            <select
                                                value={insightClientSelections[insight.id] || ''}
                                                onChange={(e) => setInsightClientSelections({ ...insightClientSelections, [insight.id]: e.target.value })}
                                                className="text-xs p-2 border border-slate-300 rounded-xl bg-slate-50 focus:bg-white flex-1 font-semibold text-slate-800 outline-none"
                                            >
                                                <option value="">-- Seleziona cliente per la cartella --</option>
                                                {clients.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                                                ))}
                                            </select>
                                            <button
                                                onClick={() => handleAcceptInsight(insight)}
                                                className="px-3.5 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 transition shadow-xs active:scale-95 flex items-center gap-1"
                                            >
                                                <span>✓</span> Accetta
                                            </button>
                                            <button
                                                onClick={() => handleRejectInsight(insight.id)}
                                                className="px-3.5 py-2 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold hover:bg-rose-100 transition active:scale-95 flex items-center gap-1"
                                            >
                                                <span>✕</span> Scarta
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL ANTEPRIMA / DETTAGLIO COMPLETO INSIGHT */}
            {selectedInsightPreview && (
                <div className="fixed inset-0 bg-black/70 z-60 flex items-center justify-center p-4 animate-fade-in backdrop-blur-xs">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
                        {/* Header */}
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-brand-100 text-brand-700 flex items-center justify-center text-xl shadow-inner">
                                    👁️
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-slate-900">Anteprima Insight del Cliente</h3>
                                    <p className="text-xs text-slate-500">Lettura integrale delle risposte ricevute dal modulo</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setSelectedInsightPreview(null)}
                                className="w-8 h-8 rounded-full bg-slate-200/80 hover:bg-slate-300 text-slate-700 font-bold flex items-center justify-center text-sm transition"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto space-y-5 flex-1">
                            {/* Submitter details */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                                <div>
                                    <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Nome / Autore</span>
                                    <p className="font-black text-slate-900 text-sm mt-0.5">
                                        {selectedInsightPreview.name || selectedInsightPreview.clientName || 'Non specificato (Anonimo)'}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Email indicata</span>
                                    <p className="font-black text-brand-800 text-sm mt-0.5">
                                        {selectedInsightPreview.email}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Data invio</span>
                                    <p className="text-xs font-semibold text-slate-700 mt-0.5">
                                        {new Date(selectedInsightPreview.submittedAt || selectedInsightPreview.timestamp || Date.now()).toLocaleString('it-IT')}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Tipologia</span>
                                    <span className="inline-block mt-0.5 px-2 py-0.5 bg-brand-100 text-brand-800 rounded font-bold text-xs">
                                        {selectedInsightPreview.type || 'Riflessione Pubblica'}
                                    </span>
                                </div>
                            </div>

                            {/* Full insight text */}
                            <div className="space-y-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                                    <span>📝</span> Testo Completo dell'Insight
                                </span>
                                <div className="p-5 bg-white border-2 border-slate-200 rounded-2xl text-slate-800 text-sm leading-relaxed whitespace-pre-wrap font-medium shadow-inner select-text">
                                    {selectedInsightPreview.notes || 'Nessun testo fornito.'}
                                </div>
                            </div>
                        </div>

                        {/* Footer actions */}
                        <div className="p-5 border-t border-slate-100 bg-slate-50 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-2 flex-1">
                                <span className="text-xs font-bold text-slate-700 whitespace-nowrap">Associa a:</span>
                                <select
                                    value={insightClientSelections[selectedInsightPreview.id] || ''}
                                    onChange={(e) => setInsightClientSelections({ ...insightClientSelections, [selectedInsightPreview.id]: e.target.value })}
                                    className="text-xs p-2.5 border border-slate-300 rounded-xl bg-white flex-1 font-bold text-slate-800 outline-none"
                                >
                                    <option value="">-- Seleziona cliente per la cartella --</option>
                                    {clients.map(c => (
                                        <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex items-center gap-2 justify-end">
                                <button
                                    onClick={async () => {
                                        await handleRejectInsight(selectedInsightPreview.id);
                                        setSelectedInsightPreview(null);
                                    }}
                                    className="px-4 py-2.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold hover:bg-rose-100 transition"
                                >
                                    ✕ Scarta
                                </button>
                                <button
                                    onClick={async () => {
                                        await handleAcceptInsight(selectedInsightPreview);
                                        setSelectedInsightPreview(null);
                                    }}
                                    className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition shadow-sm active:scale-95"
                                >
                                    ✓ Accetta nella Cartella
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Group Counseling Modal */}
            {showGroupModal && (
                <GroupCounselingModal
                    clients={clients}
                    counselor={user}
                    counselorsList={counselorsList}
                    onClose={() => setShowGroupModal(false)}
                />
            )}

            {/* Counselor Management Modal (for Admin) */}
            {isAdmin && (
                <CounselorManagementModal
                    isOpen={showCounselorModal}
                    onClose={() => setShowCounselorModal(false)}
                    currentUser={user}
                    onCounselorsUpdated={fetchClients}
                />
            )}

            {/* Supervision Access & Grants Log Modal (for SuperAdmin & Counselors) */}
            {showAccessLogModal && (
                <SupervisionAccessLogModal
                    isOpen={showAccessLogModal}
                    onClose={() => setShowAccessLogModal(false)}
                    clients={clients}
                    counselorsList={counselorsList}
                    user={user}
                    onOpenClient={openClient}
                    onRefresh={fetchClients}
                />
            )}

            {/* Organization Management Modal (Anagrafica Enti) */}
            {showOrgModal && (
                <OrganizationManagementModal
                    isOpen={showOrgModal}
                    onClose={() => setShowOrgModal(false)}
                    onOrganizationUpdated={fetchClients}
                />
            )}

            {/* Quick Reassign Modal for Direct Card Action */}
            {clientToReassign && (
                <div 
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in"
                    onClick={() => setClientToReassign(null)}
                >
                    <div 
                        className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-100 space-y-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                            <div className="flex items-center gap-2.5">
                                <span className="text-2xl p-2 bg-indigo-50 rounded-2xl">🔄</span>
                                <div>
                                    <h3 className="text-base font-bold text-slate-800">Riassegna Cliente</h3>
                                    <p className="text-xs text-slate-500 font-medium">{clientToReassign.name || clientToReassign.email}</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setClientToReassign(null)} 
                                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 text-lg transition cursor-pointer"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200/60">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Counselor Attualmente Assegnato</span>
                                <p className="text-xs font-bold text-slate-800">
                                    {(() => {
                                        const currC = counselorsList.find(c => 
                                            c.id === clientToReassign.counselorId || 
                                            (c.email && clientToReassign.counselorEmail && c.email.toLowerCase() === clientToReassign.counselorEmail.toLowerCase()) ||
                                            (c.email && clientToReassign.counselorId && c.email.toLowerCase() === clientToReassign.counselorId.toLowerCase())
                                        );
                                        return currC ? `${currC.name} (${currC.email})` : (clientToReassign.counselorName || clientToReassign.counselorEmail || 'Non assegnato');
                                    })()}
                                </p>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-indigo-950 mb-1.5">Nuovo Counselor di Riferimento</label>
                                <select
                                    value={quickReassignTargetCounselorId}
                                    onChange={(e) => setQuickReassignTargetCounselorId(e.target.value)}
                                    className="w-full p-2.5 text-xs border border-indigo-200 rounded-xl bg-white outline-none focus:ring-2 focus:ring-indigo-400 font-medium text-slate-800"
                                >
                                    <option value="">-- Seleziona Counselor di Destinazione --</option>
                                    {(isAdmin ? counselorsList : counselorsList.filter(c => c.canOrganizational !== false)).map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.name} ({c.email})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-2 justify-end pt-3 border-t border-slate-100">
                            <button
                                type="button"
                                onClick={() => setClientToReassign(null)}
                                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                disabled={quickReassigning || !quickReassignTargetCounselorId}
                                onClick={async () => {
                                    const targetC = counselorsList.find(c => c.id === quickReassignTargetCounselorId);
                                    if (!targetC) return;
                                    try {
                                        setQuickReassigning(true);
                                        await DataManager.reassignClientCounselor(
                                            clientToReassign.id,
                                            targetC.id,
                                            targetC.email,
                                            targetC.name
                                        );
                                        setClientToReassign(null);
                                        await fetchClients();
                                        alert(`✅ Cliente "${clientToReassign.name || clientToReassign.email}" riassegnato con successo a ${targetC.name || targetC.email}!`);
                                    } catch (err) {
                                        console.error("Errore riassegnazione:", err);
                                        alert("Errore durante la riassegnazione: " + err.message);
                                    } finally {
                                        setQuickReassigning(false);
                                    }
                                }}
                                className="px-5 py-2 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition disabled:opacity-50 cursor-pointer shadow-sm active:scale-95"
                            >
                                {quickReassigning ? 'Riassegnazione in corso...' : 'Conferma Riassegnazione'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Privacy Policy & GDPR Modal */}
            <PrivacyPolicyModal
                isOpen={showPrivacyModal}
                onClose={() => setShowPrivacyModal(false)}
            />
        </div>
    );
};

export default CounselorDashboard;
